import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Puff } from 'react-loader-spinner'
import { BiTrash, BiTrophy } from 'react-icons/bi'
import { getAchievements, deleteAchievement } from '../../actions/achievements'
import Li from '../../components/Li'
import Button from '../../components/Button'
import Confirmation from '../../components/Confirmation/Confirmation'
import AchievementModal from './Modal'
import styles from './styles.module.scss'

const Achievements = () => {
  const dispatch = useDispatch()
  const { list, loading } = useSelector(state => state.achievements)

  const [selectedItem, setSelectedItem] = useState(null)
  const [confirmation, setConfirmation] = useState(null)

  useEffect(() => {
    dispatch(getAchievements())
  }, [dispatch])

  const handleDelete = useCallback((item) => (e) => {
    e.stopPropagation()
    setConfirmation({ id: item.id, name: item.name })
  }, [])

  const openModal = useCallback((data) => () => {
    setSelectedItem(data || {})
  }, [])

  const closeModal = useCallback(() => {
    setSelectedItem(null)
  }, [])

  const acceptConfirmation = useCallback(async () => {
    if (!confirmation) return
    await dispatch(deleteAchievement(confirmation.id))
    setConfirmation(null)
  }, [dispatch, confirmation])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(null)
  }, [])

  const isModalOpen = selectedItem !== null
  const isAddMode = selectedItem && !selectedItem.id

  const sortedList = useMemo(() => {
    if (!list) return []
    return [...list].sort((a, b) => a.name?.localeCompare(b.name))
  }, [list])

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <BiTrophy size={64} className={styles.emptyIcon} />
      <p>Нет добавленных достижений</p>
      <Button onClick={openModal()}>Добавить первое достижение</Button>
    </div>
  )

  const renderLoading = () => (
    <div className={styles.loadingState}>
      <Puff
        height={50}
        width={50}
        color="#3689FF"
        ariaLabel="Загрузка..."
      />
      <span>Загрузка достижений...</span>
    </div>
  )

  const renderList = () => (
    <div className={styles.listContainer}>
      <ul className={styles.list}>
        {sortedList.map((item) => (
          <Li
            key={item.id}
            onClick={openModal(item)}
            className={styles.listItem}
          >
            <div className={styles.itemContent}>
              <BiTrophy size={20} className={styles.itemIcon} />
              <span className={styles.itemName}>{item.name}</span>
              {item.alias && (
                <span className={styles.itemAlias}>({item.alias})</span>
              )}
            </div>
            <button
              type="button"
              className={styles.deleteBtn}
              onClick={handleDelete(item)}
              title="Удалить достижение"
              aria-label={`Удалить ${item.name}`}
            >
              <BiTrash size={22} />
            </button>
          </Li>
        ))}
      </ul>
      <div className={styles.actions}>
        <Button onClick={openModal()}>Добавить достижение</Button>
        <span className={styles.counter}>
          Всего: {sortedList.length}
        </span>
      </div>
    </div>
  )

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h1>Виды достижений</h1>
      </header>

      <main className={styles.content}>
        {loading && renderLoading()}
        {!loading && sortedList.length === 0 && renderEmptyState()}
        {!loading && sortedList.length > 0 && renderList()}
      </main>

      <AchievementModal
        open={isModalOpen}
        onClose={closeModal}
        data={selectedItem}
        addMode={isAddMode}
      />

      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Вы уверены, что хотите удалить "${confirmation.name}"?`}
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
        />
      )}
    </div>
  )
}

export default Achievements
